// Level-Up System imports (Phase 26A & 26B)
import type { ExperienceEntry, MarkedTraits, TierAdvancementState, Tier, LevelUpChanges, Level } from './levelUp';

// Equipment System imports (Phase 1)
import type { LootReference, ConsumableReference } from './equipment';

// Character Biography interfaces (Phase 49) - Enhanced for Phase 2
export interface CharacterBiography {
  appearance?: CharacterAppearance;
  backgroundAnswers?: BackgroundAnswer[];
  connectionAnswers?: ConnectionAnswer[];
  startingChoices?: StartingChoice[];
  // Phase 2 additions
  characterJournal?: JournalEntry[];
  customFields?: CustomBiographyField[];
  relationships?: CharacterRelationship[];
}

export interface CharacterAppearance {
  clothes?: string;
  eyes?: string;
  body?: string;
  skin?: string;
  attitude?: string;
}

export interface BackgroundAnswer {
  question: string;
  answer: string;
}

export interface ConnectionAnswer {
  prompt: string;
  answer: string;
}

export interface StartingChoice {
  category: string;
  choice: string;
  description?: string;
}

// Phase 2 Enhanced Biography interfaces
export interface JournalEntry {
  id: string;
  date: string; // ISO date string for Firebase compatibility
  sessionNumber?: number;
  title: string;
  content: string;
  tags: string[];
  importance: 'low' | 'medium' | 'high';
}

export interface CustomBiographyField {
  id: string;
  label: string;
  value: string;
  type: 'text' | 'textarea' | 'list';
  order: number;
}

export interface CharacterRelationship {
  id: string;
  name: string;
  type: 'ally' | 'rival' | 'neutral' | 'mentor' | 'student' | 'family' | 'romantic' | 'enemy';
  description: string;
  notes: string;
  strength: number; // 1-5 scale
  history: RelationshipEvent[];
}

export interface RelationshipEvent {
  id: string;
  date: Date;
  description: string;
  impact: 'positive' | 'negative' | 'neutral';
}

/**
 * Multiclass data - represents an additional class taken at level 5+
 */
export interface MulticlassData {
  classId: string;
  className: string;
  classFeature: string;
  classFeatureDescription: string;

  // Domain selection
  domainId: string;
  domainName: string;

  // Subclass foundation card
  subclassId: string;
  subclassName: string;
  foundationCardId: string;
  foundationCardName: string;

  // Tracking
  acquiredAtLevel: Level;
  acquiredAtTier: Tier;
}

export interface CharacterData {
  id?: string;
  name: string;
  level: string;
  class: string;
  heritage: string;
  community: string;
  tier: number;
  subclass?: string;
  subclassCardTier?: 'foundation' | 'specialization' | 'mastery'; // Tracks current subclass card progression

  // Character Biography Data (Phase 49)
  biography: CharacterBiography;

  // Level-up system additions (Phase 26A)
  experiences?: ExperienceEntry[]; // Proper format with id, name, bonus, createdAtLevel
  markedTraits?: MarkedTraits;
  proficiency: number; // 1-6 (starts at 1, required for all characters)

  // Tier advancement tracking (persisted across level-ups)
  tierAdvancements?: {
    [tier in Tier]?: TierAdvancementState;
  };

  // Level-up history (for tracking and potential rollback)
  levelUpHistory?: {
    level: Level;
    sessionId: string;
    completedAt: number;
    changes: LevelUpChanges;
  }[];

  // Multiclass data (Phase 26B)
  multiclass?: MulticlassData;
  
  // NEW: Reference-based inventory system
  inventory: CharacterInventory;
  
  // Base stats nested structure
  baseStats: {
    traits: {
      agility: number;
      strength: number;
      finesse: number;
      instinct: number;
      presence: number;
      knowledge: number;
    };
    evasion: number;
    armor: number;
    hpSlots: number;
    stressSlots: number;
    hope: number;
    thresholds: {
      major: number;
      severe: number;
    };
  };

  // Manual overrides for base stats (optional)
  // When set, these values override computed stats in ConfigurationPanel
  // Setting a value to undefined removes the override and reverts to computed value
  manualOverrides?: {
    evasion?: number;
    armor?: number;
    hpSlots?: number;
    stressSlots?: number;
    hope?: number;
    thresholdMajor?: number;
    thresholdSevere?: number;
  };

  // Current state nested structure
  currentState: {
    hpMarked: number[];
    stressMarked: number[];
    armorMarked: number[];
    currentHope: number;
  };
  
  // Flattened properties for component compatibility
  evasion: number;
  armorSlots: number;
  hpSlots: number;
  stressSlots: number;
  maxHope: number;
  thresholds: {
    major: number;
    severe: number;
  };
  hpMarked: number[];
  stressMarked: number[];
  armorMarked: number[];
  currentHope: number;
  traits: {
    agility: number;
    strength: number;
    finesse: number;
    instinct: number;
    presence: number;
    knowledge: number;
  };
  
  activeModifiers: Modifier[];
  // LEGACY: Remove after migration to reference-based system
  equipment: EquipmentItem[];
  classFeatures: ClassFeature[];
  classSpecificData: ClassSpecificData;
  // REMOVED: activeSpells array - replaced with vault/loadout system in inventory
}

export interface ComputedStats {
  traits: {
    agility: number;
    strength: number;
    finesse: number;
    instinct: number;
    presence: number;
    knowledge: number;
  };
  evasion: number;
  armor: number;
  hpSlots: number;
  stressSlots: number;
  hope: number;
  thresholds: {
    major: number;
    severe: number;
  };
}

export interface Modifier {
  id: string;
  source: ModifierSource;
  target: string;
  operation: ModifierOperation;
  value: number | ModifierFormulaObject;
  conditions?: ModifierCondition[];
  stackable: boolean;
  priority: number;
}

export interface ModifierSource {
  type: 'equipment' | 'spell' | 'class' | 'heritage' | 'stance' | 'temporary';
  id: string;
  name: string;
}

export type ModifierOperation = 'add' | 'multiply' | 'set' | 'replace';

export interface ModifierFormulaObject {
  type: 'trait_direct' | 'trait_half' | 'trait_multiply' | 'character_level' | 'character_tier' | 'expression';
  trait?: 'agility' | 'strength' | 'finesse' | 'instinct' | 'presence' | 'knowledge';
  value?: number;       // Base value to add to trait (e.g., "strength + 3" for bare-bones)
  multiplier?: number;  // Required for trait_multiply
  formula?: string;     // Required for expression
  dependencies?: string[]; // Required for expression, auto-computed for others
}

// Legacy interface for backward compatibility during migration
export interface ModifierFormula {
  formula: string;
  dependencies: string[];
}

export interface ModifierCondition {
  type: 'equipment_not_equipped' | 'stat_greater_than' | 'stat_less_than' | 'tier_equals' | 'equipment' | 'domain_card_count';
  category?: string;
  equipped?: boolean;     // For equipment conditions
  domain?: string;        // For domain_card_count conditions  
  count?: number;         // For domain_card_count conditions
  stat?: string;
  value?: number;
}

// NEW: Unified reference-based content system
export interface ContentReference {
  contentId: string;        // Reference to Content Library item ID
  contentType: 'equipment' | 'domain_card';
  quantity?: number;        // For equipment (default: 1 for domain cards)
  equippedCount?: number;   // For equipment only
}

// Legacy equipment reference for backward compatibility
export interface EquipmentReference {
  itemId: string;           // Reference to Content Library item ID
  quantity: number;         // Total number of this item owned
  equippedCount: number;    // Number currently equipped (0 <= equippedCount <= quantity)
}

export interface CharacterInventory {
  equipmentReferences: EquipmentReference[];     // LEGACY: Will be migrated to ContentReference[]

  // Vault & Loadout System (Phase 48)
  domainCardVault: ContentReference[];          // Inactive domain cards (no stat effects)
  domainCardLoadout: ContentReference[];        // Active domain cards (max 5, apply stat effects)

  // Equipment System (Phase 1)
  lootReferences?: LootReference[];             // Loot items (reusable equipment)
  consumableReferences?: ConsumableReference[]; // Consumable items (single-use)

  manualItems: InventoryItem[];                 // User-created items (no change to this)
}

export interface InventoryItem {
  name: string;
  description: string;
  quantity: number;
}

// LEGACY: Keep for backwards compatibility during transition (will be removed)
export interface EquipmentItem {
  id: string;
  name: string;
  type: 'weapon' | 'armor';
  description: string;
  modifiers: Modifier[];
  equipped: boolean;
  requirements?: EquipmentRequirement[];
  // Content Library additional properties
  category?: string;
  cost?: { gp?: number; sp?: number; cp?: number };
  tags?: string[];
}

export interface EquipmentRequirement {
  type: 'stat_minimum';
  stat: string;
  value: number;
}

export interface Spell {
  id: string;
  name: string;
  type: 'passive' | 'active' | 'concentration';
  description: string;
  modifiers: Modifier[];
  duration?: SpellDuration;
  domain?: string;  // For domain card count conditions
}

export interface SpellDuration {
  type: 'permanent' | 'combat' | 'rounds' | 'minutes' | 'hours';
  value?: number;
}

export interface ClassFeature {
  id: string;
  name: string;
  description: string;
  modifiers: Modifier[];
  tier: number;
  active: boolean;
}

// Class-specific data structures
export interface ClassSpecificData {
  brawler?: BrawlerData;
}

export interface BrawlerData {
  comboDie: string;
  activeStance: string;
  martialStances: string[];
  focusTokens: number;
  maxFocusTokens: number;
}

// Helper functions for class-specific data
export const createDefaultBrawlerData = (): BrawlerData => ({
  comboDie: "d4",
  activeStance: "none",
  martialStances: [],
  focusTokens: 0,
  maxFocusTokens: 0,
});

export const getBrawlerData = (character: CharacterData): BrawlerData => {
  return character.classSpecificData.brawler || createDefaultBrawlerData();
};

export const updateBrawlerData = (
  character: CharacterData, 
  updates: Partial<BrawlerData>
): CharacterData => {
  return {
    ...character,
    classSpecificData: {
      ...character.classSpecificData,
      brawler: {
        ...getBrawlerData(character),
        ...updates
      }
    }
  };
};

// Normalize a character loaded from Firebase to ensure all required fields are present
export const normalizeCharacterData = (character: any): CharacterData => {
  if (!character) {
    throw new Error('Cannot normalize null character data');
  }

  // Ensure biography object exists with all required arrays
  const normalizedBiography = {
    appearance: character.biography?.appearance,
    backgroundAnswers: character.biography?.backgroundAnswers,
    connectionAnswers: character.biography?.connectionAnswers,
    startingChoices: character.biography?.startingChoices,
    customFields: character.biography?.customFields || [],
    characterJournal: character.biography?.characterJournal || [],
    relationships: character.biography?.relationships || []
  };

  // Ensure inventory object exists with all required arrays (Phase 1 equipment system)
  const normalizedInventory = {
    ...character.inventory,
    lootReferences: character.inventory?.lootReferences || [],
    consumableReferences: character.inventory?.consumableReferences || []
  };

  return {
    ...character,
    biography: normalizedBiography,
    inventory: normalizedInventory
  } as CharacterData;
};

export const createDefaultCharacter = (): CharacterData => ({
  name: "",
  level: "1",
  class: "",
  heritage: "",
  community: "",
  tier: 1,
  proficiency: 1,
  subclass: "",
  subclassCardTier: 'foundation', // Default to foundation when subclass is chosen
  experiences: [],
  biography: {
    appearance: undefined,
    backgroundAnswers: undefined,
    connectionAnswers: undefined,
    startingChoices: undefined,
    customFields: [],
    characterJournal: [],
    relationships: []
  },
  inventory: {
    equipmentReferences: [],
    domainCardVault: [],
    domainCardLoadout: [],
    lootReferences: [],
    consumableReferences: [],
    manualItems: []
  },
  baseStats: {
    traits: {
      agility: 0,
      strength: 0,
      finesse: 0,
      instinct: 0,
      presence: 0,
      knowledge: 0
    },
    evasion: 11,
    armor: 0,
    hpSlots: 6,
    stressSlots: 6,
    hope: 6,
    thresholds: {
      major: 1,
      severe: 2
    }
  },
  currentState: {
    hpMarked: [],
    stressMarked: [],
    armorMarked: [],
    currentHope: 2
  },
  // Flattened properties for component compatibility
  evasion: 11,
  armorSlots: 0,
  hpSlots: 6,
  stressSlots: 6,
  maxHope: 6,
  thresholds: {
    major: 1,
    severe: 2
  },
  hpMarked: [],
  stressMarked: [],
  armorMarked: [],
  currentHope: 2,
  traits: {
    agility: 0,
    strength: 0,
    finesse: 0,
    instinct: 0,
    presence: 0,
    knowledge: 0
  },
  activeModifiers: [],
  equipment: [],
  classFeatures: [],
  classSpecificData: {}
});